import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link2, Search, Tag, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib/catalog-admin`;
const money = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Search the consumables module and pick a priced item, or type a one-off cost. onPick({consumable_ref}) | onPick({unit_cost})
export default function ConsumablePicker({ onPick, onClose, initialQuery = '', allowManual = true, busy = false, testId = 'consumable-picker' }) {
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState([]);
  const [manual, setManual] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setSearching(true);
      try { const r = await axios.get(`${API}/price-sources`, { params: { q, type: 'consumable' } }); setRows((r.data.results || []).slice(0, 40)); }
      catch { setRows([]); }
      setSearching(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="bg-white border border-blue-300 rounded-xl p-2.5 space-y-2 w-full max-w-md shadow-lg" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Search className="w-4 h-4 text-slate-500 shrink-0" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the consumables module… e.g. zipcut 5" className="flex-1 h-9 px-2 rounded-lg border border-slate-300 text-sm" data-testid={`${testId}-search`} />
        {onClose && <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" data-testid={`${testId}-close`}><X className="w-4 h-4" /></button>}
      </div>
      <ul className="max-h-56 overflow-y-auto divide-y divide-slate-100 text-sm" data-testid={`${testId}-results`}>
        {rows.length === 0 && <li className="py-2 text-xs text-slate-500">{searching ? 'Searching…' : 'No consumable matches — try fewer words, or type a cost below.'}</li>}
        {rows.map((c) => (
          <li key={c.id}>
            <button type="button" disabled={busy || c.unit_cost == null} onClick={() => onPick({ consumable_ref: c.id, name: c.name, unit_cost: c.unit_cost })}
              className="w-full text-left py-1.5 px-1 flex items-center gap-2 hover:bg-blue-50 rounded disabled:opacity-50" data-testid={`${testId}-pick-${c.id}`}>
              <Link2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-slate-800">{c.name} <span className="text-xs text-slate-500">· {c.category}</span></span>
              <b className="text-slate-900 shrink-0">{c.unit_cost != null ? `${money(c.unit_cost)}/${c.unit || 'each'}` : 'no price'}</b>
            </button>
          </li>
        ))}
      </ul>
      {allowManual && (
        <div className="flex items-center gap-1.5 pt-1 border-t border-slate-100">
          <span className="text-xs text-slate-600">or type a cost</span>
          <span className="text-slate-500 text-xs">$</span>
          <input type="number" min="0" step="0.01" inputMode="decimal" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="0.00" className="w-24 h-9 px-2 rounded-lg border border-slate-300 text-sm font-semibold" data-testid={`${testId}-manual`} />
          <button type="button" disabled={busy || manual === ''} onClick={() => onPick({ unit_cost: Number(manual) })} className="h-9 px-2.5 rounded-lg bg-white text-slate-900 text-xs font-bold disabled:opacity-50 inline-flex items-center gap-1" data-testid={`${testId}-manual-save`}><Tag className="w-3.5 h-3.5" /> Set</button>
        </div>
      )}
    </div>
  );
}

export { money };
